// AbortError detection for fetch / DOMException callers.
//
// Modern browsers throw a DOMException with `name === 'AbortError'` when a
// fetch is interrupted by an AbortController. Legacy browsers (older Safari
// builds, some WebViews) instead expose the abort via `code === 20`
// (DOMException.ABORT_ERR). Both shapes must be treated as the same outcome
// so callers can swallow the abort without misreporting it.
//
// Convention: this predicate is deliberately permissive — it matches any
// object whose `name === 'AbortError'` OR whose numeric `code === 20`. The
// raw `code === 20` branch could in principle false-positive on an unrelated
// DOMException-shaped object that uses code 20 for a different meaning. In
// practice the DOMException spec reserves code 20 exclusively for ABORT_ERR
// (https://webidl.spec.whatwg.org/#idl-DOMException-error-names), and neither
// call site can meet another error carrying it. New call sites should weigh
// the same constraint.
//
// Both call sites are in app.component.ts, and both distinguish a fetch the
// app itself tore down from a real error that deserves a Sentry
// trackSilentProblem: the checkout-redirect catch, where `reloadAppAsync` can
// interrupt an in-flight userSubscription request, and the global `window.fetch`
// wrapper, where any caller cancelling its own request would otherwise be
// reported as a network failure (#5410).
//
// A timeout is deliberately NOT matched: a deadline set with
// `AbortSignal.timeout` rejects with a TimeoutError (code 23), and a request
// that ran out of time is a real problem worth reporting.

export function isAbortError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as { name?: unknown; code?: unknown };
  return err.name === 'AbortError' || err.code === 20;
}
